import React, { useState } from "react";
import { FormGroup, FormControl, ControlLabel, Checkbox, Button, InputGroup } from "react-bootstrap";
import "./PasswordGenerator.css";

export default function PasswordGenerator({ onPasswordGenerated }) {
  const [password, setPassword] = useState("");
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
    excludeAmbiguous: false
  });
  const [copied, setCopied] = useState(false);

  const charSets = {
    uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    lowercase: "abcdefghijklmnopqrstuvwxyz",
    numbers: "0123456789",
    symbols: "!@#$%^&*()_+-=[]{}|;:,.<>?"
  };

  const ambiguousChars = "il1Lo0O";

  function generatePassword() {
    let charset = "";
    let generatedPassword = "";

    // Build character set based on options
    if (options.uppercase) charset += charSets.uppercase;
    if (options.lowercase) charset += charSets.lowercase;
    if (options.numbers) charset += charSets.numbers;
    if (options.symbols) charset += charSets.symbols;

    // Remove ambiguous characters if option is selected
    if (options.excludeAmbiguous) {
      charset = charset.split("").filter(char => !ambiguousChars.includes(char)).join("");
    }

    // Generate password
    if (charset.length === 0) {
      alert("Please select at least one character type");
      return;
    }

    // Ensure at least one character from each selected type
    const requiredChars = [];
    if (options.uppercase) requiredChars.push(charSets.uppercase[Math.floor(Math.random() * charSets.uppercase.length)]);
    if (options.lowercase) requiredChars.push(charSets.lowercase[Math.floor(Math.random() * charSets.lowercase.length)]);
    if (options.numbers) requiredChars.push(charSets.numbers[Math.floor(Math.random() * charSets.numbers.length)]);
    if (options.symbols) requiredChars.push(charSets.symbols[Math.floor(Math.random() * charSets.symbols.length)]);

    // Fill remaining length with random characters
    for (let i = requiredChars.length; i < length; i++) {
      generatedPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    // Add required characters and shuffle
    generatedPassword = (generatedPassword + requiredChars.join(""))
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    setPassword(generatedPassword);
  }

  function copyToClipboard() {
    if (!password) return;
    
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function usePassword() {
    if (!password) return;
    
    // Call callback if provided
    if (onPasswordGenerated) {
      onPasswordGenerated(password);
    }
  }

  function handleOptionChange(option) {
    setOptions({ ...options, [option]: !options[option] });
  }

  return (
    <div className="PasswordGenerator">
      <h4>Password Generator</h4>
      
      <FormGroup>
        <ControlLabel>Generated Password</ControlLabel>
        <InputGroup>
          <FormControl
            type="text"
            value={password}
            readOnly
            placeholder="Click 'Generate' to create a password"
          />
          <InputGroup.Button>
            <Button onClick={copyToClipboard} disabled={!password}>
              {copied ? "✓ Copied!" : "Copy"}
            </Button>
          </InputGroup.Button>
        </InputGroup>
      </FormGroup>

      <FormGroup>
        <ControlLabel>Length: {length}</ControlLabel>
        <FormControl
          type="range"
          min="8"
          max="64"
          value={length}
          onChange={(e) => setLength(parseInt(e.target.value))}
        />
      </FormGroup>

      <FormGroup>
        <ControlLabel>Options</ControlLabel>
        <Checkbox
          checked={options.uppercase}
          onChange={() => handleOptionChange("uppercase")}
        >
          Uppercase (A-Z)
        </Checkbox>
        <Checkbox
          checked={options.lowercase}
          onChange={() => handleOptionChange("lowercase")}
        >
          Lowercase (a-z)
        </Checkbox>
        <Checkbox
          checked={options.numbers}
          onChange={() => handleOptionChange("numbers")}
        >
          Numbers (0-9)
        </Checkbox>
        <Checkbox
          checked={options.symbols}
          onChange={() => handleOptionChange("symbols")}
        >
          Symbols (!@#$%...)
        </Checkbox>
        <Checkbox
          checked={options.excludeAmbiguous}
          onChange={() => handleOptionChange("excludeAmbiguous")}
        >
          Exclude Ambiguous Characters (i, l, 1, L, o, 0, O)
        </Checkbox>
      </FormGroup>

      <Button 
        bsStyle="primary" 
        onClick={generatePassword}
        block
      >
        Generate Password
      </Button>
      
      {onPasswordGenerated && password && (
        <Button 
          bsStyle="success" 
          onClick={usePassword}
          block
          style={{ marginTop: '10px' }}
        >
          Use This Password
        </Button>
      )}
    </div>
  );
}
